// src/utils/timeSlotGenerator.js

/**
 * Generate available time slots based on store's week_schedule
 * Handles multiple schedules per day and break times
 * Automatically filters out past time slots
 */

/**
 * Convert "HH:MM" string to minutes since midnight
 */
const timeToMinutes = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Convert minutes since midnight to "HH:MM" format
 */
const minutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

/**
 * Format time for display (HH:MM to 12-hour format)
 */
export const formatTimeDisplay = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

/**
 * Get day name from Date object
 */
const getDayName = (date) => {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[date.getDay()];
};

/**
 * Check if a time slot is in the past
 * Adds a buffer time to ensure users can't select slots that are too soon
 */
const isTimeInPast = (date, timeStr, currentDate = new Date(), bufferMinutes = 30) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const slotDate = new Date(date);
  slotDate.setHours(hours, minutes, 0, 0);
  
  // Add buffer time (default: 30 minutes from now)
  const minAllowedTime = new Date(currentDate.getTime() + bufferMinutes * 60000);
  
  return slotDate <= minAllowedTime;
};

/**
 * Generate time slots for a specific day
 * @param {Date} date - The date to generate slots for
 * @param {Object} weekSchedule - The week_schedule object from branch data
 * @param {number} slotDuration - Duration of each slot in minutes (default: 30)
 * @param {number} bufferMinutes - Buffer time in minutes to prevent immediate orders (default: 30)
 * @returns {Array} Array of time slot objects { time, display, disabled, reason }
 */
export const generateTimeSlotsForDay = (date, weekSchedule, slotDuration = 30, bufferMinutes = 30) => {
  if (!weekSchedule) return [];
  
  const dayName = getDayName(date);
  const daySchedule = weekSchedule[dayName];
  
  if (!daySchedule || !daySchedule.is_open) {
    return [];
  }
  
  const slots = [];
  const currentDate = new Date();
  const isToday = date.toDateString() === currentDate.toDateString();
  
  // Process each schedule block for the day
  const schedules = daySchedule.schedules || [];
  
  schedules.forEach((schedule) => {
    const openTime = timeToMinutes(schedule.open_time);
    const closeTime = timeToMinutes(schedule.close_time);
    
    // Generate slots from open to close time
    let currentSlot = openTime;
    
    while (currentSlot + slotDuration <= closeTime) {
      const timeStr = minutesToTime(currentSlot);
      const displayTime = formatTimeDisplay(timeStr);
      
      // Check if this slot is in the past (only for today)
      const isPast = isToday && isTimeInPast(date, timeStr, currentDate, bufferMinutes);
      
      // Only add slot if it's not in the past OR it's a future date
      if (!isPast || !isToday) {
        slots.push({
          time: timeStr,
          display: displayTime,
          disabled: false,
          reason: '',
          dateStr: formatLocalDate(date) // YYYY-MM-DD (local, not UTC)
        });
      }
      
      currentSlot += slotDuration;
    }
  });
  
  return slots;
};

/**
 * Generate time slots for next N days
 * Automatically filters out past time slots for today
 * @param {Object} weekSchedule - The week_schedule object
 * @param {number} days - Number of days to generate (default: 7)
 * @param {number} slotDuration - Duration of each slot in minutes (default: 30)
 * @param {number} bufferMinutes - Buffer time in minutes (default: 30)
 * @returns {Object} Object with dates as keys and slot arrays as values
 */
export const generateTimeSlots = (weekSchedule, days = 7, slotDuration = 30, bufferMinutes = 30) => {
  if (!weekSchedule) return {};
  
  const slots = {};
  const today = new Date();
  
  for (let i = 0; i < days; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    
    const dateKey = formatLocalDate(date); // YYYY-MM-DD (local, not UTC)
    const daySlots = generateTimeSlotsForDay(date, weekSchedule, slotDuration, bufferMinutes);
    
    // Only include dates that have available slots
    if (daySlots.length > 0) {
      slots[dateKey] = {
        date: dateKey,
        dayName: date.toLocaleDateString('en-US', { weekday: 'long' }),
        displayDate: date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
        }),
        slots: daySlots
      };
    }
  }
  
  return slots;
};

/**
 * Get next available time slot
 * @param {Object} weekSchedule - The week_schedule object
 * @returns {Object|null} { date, time, display } or null
 */
export const getNextAvailableSlot = (weekSchedule) => {
  const slots = generateTimeSlots(weekSchedule, 7, 30);
  
  for (const dateKey of Object.keys(slots).sort()) {
    const daySlots = slots[dateKey].slots.filter(s => !s.disabled);
    if (daySlots.length > 0) {
      return {
        date: dateKey,
        time: daySlots[0].time,
        display: `${slots[dateKey].displayDate} at ${daySlots[0].display}`
      };
    }
  }
  
  return null;
};

/**
 * Check if store is currently open for immediate collection
 * @param {Object} weekSchedule - The week_schedule object
 * @returns {boolean}
 */
export const isStoreOpenNow = (weekSchedule) => {
  if (!weekSchedule) return false;
  
  const now = new Date();
  const dayName = getDayName(now);
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  
  const daySchedule = weekSchedule[dayName];
  
  if (!daySchedule || !daySchedule.is_open) return false;
  
  const schedules = daySchedule.schedules || [];
  
  return schedules.some(schedule => {
    const openTime = timeToMinutes(schedule.open_time);
    const closeTime = timeToMinutes(schedule.close_time);
    return currentMinutes >= openTime && currentMinutes < closeTime;
  });
};

/**
 * Format selected time for order payload (YYYY-MM-DD HH:MM:SS)
 * @param {string} dateStr - Date string (YYYY-MM-DD)
 * @param {string} timeStr - Time string (HH:MM)
 * @returns {string} Formatted datetime
 */
export const formatOrderDateTime = (dateStr, timeStr) => {
  return `${dateStr} ${timeStr}:00`;
};

/**
 * Format a Date as "YYYY-MM-DD HH:MM:SS" in LOCAL time (the branch's timezone).
 * This is what the order API expects for delivery_time / scheduled_at.
 *
 * Do NOT use Date.prototype.toISOString() for this: that returns UTC, and the
 * backend then reads the string as local time — shifting the delivery time by
 * the whole UTC offset (e.g. −5:30 in IST), which can push it into the past.
 *
 * @param {Date|string|number} date
 * @returns {string|null} "YYYY-MM-DD HH:MM:SS" or null if the input isn't a valid date
 */
export const formatLocalDateTime = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
};

/**
 * Format a Date as "YYYY-MM-DD" in LOCAL time.
 *
 * Same reason as formatLocalDateTime: date.toISOString().split('T')[0] returns
 * the UTC calendar date, which rolls back a day for any local time before the
 * UTC offset (e.g. before 05:30 in IST) — shifting every generated slot date.
 *
 * @param {Date|string|number} date
 * @returns {string|null} "YYYY-MM-DD" or null if the input isn't a valid date
 */
export const formatLocalDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};